import { useEffect, useRef, useState } from 'react';
import { GetStaticProps } from 'next';
import styled from 'styled-components';
import { Box } from '@99designs/emu';
import { mediaQuery } from '@99designs/emu/css';
import { colors, spaces } from '@99designs/emu/settings';
import { Site } from '@99designs/frontend-components/apps/global-nav/lib/Site';
import { Desktop } from '@99designs/marketing-ui';
import SiteFooterStyleOverride from '../../components/common/SiteFooter';
import Intro from '../../components/pages/design-without-borders/0-Intro';
import Diversity from '../../components/pages/design-without-borders/1-Diversity';
import Creativity from '../../components/pages/design-without-borders/2-Creativity';
import Balance from '../../components/pages/design-without-borders/3-Balance';
import Agencies from '../../components/pages/design-without-borders/4-Agencies';
import Future from '../../components/pages/design-without-borders/5-Future';
import About99designs from '../../components/pages/design-without-borders/6-About99designs';
import Head from '../../components/pages/design-without-borders/Head';
import Nav from '../../components/pages/design-without-borders/Nav';
import SideNavigation from '../../components/pages/design-without-borders/SideNavigation';
import {
    DWB21_Blue,
    DWB21_Cream,
    DWB21_Green,
    DWB21_Peach,
    DWB21_Purple,
    DWB21_Teal,
} from '../../components/pages/design-without-borders/colors';
import { getHostForLocale } from '../../lib/getHostForLocale';
import { loadSiteConfig } from '../../lib/loadSiteConfig';
import { loadTranslations } from '../../lib/loadTranslations';
import type { SiteConfig } from '@99designs/frontend-components/apps/global-nav/lib/Site';

export interface SectionData {
    id: string;
    backgroundColor: string;
}

const SectionWrapper = styled(Box)<{ backgroundColor: string }>`
    min-height: 900px;
    background: ${({ backgroundColor }) => backgroundColor};
    padding: 80px 5%;
    display: flex;
    justify-content: center;
    ${mediaQuery.only('medium')} {
        padding: 56px 10%;
    }

    ${mediaQuery.only('small')} {
        padding: 56px 20px;
    }
`;

type DesignWithoutBordersPageProps = {
    siteConfig: string;
    locale: string;
};

export default function DesignWithoutBordersPage({ siteConfig }: DesignWithoutBordersPageProps) {
    const site = new Site(JSON.parse(siteConfig) as SiteConfig);
    const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const sections = {
        intro: { id: 'intro', backgroundColor: DWB21_Cream },
        diversity: { id: 'diversity', backgroundColor: DWB21_Cream },
        creativity: { id: 'creativity', backgroundColor: DWB21_Teal },
        balance: { id: 'balance', backgroundColor: DWB21_Peach },
        agencies: { id: 'agency', backgroundColor: DWB21_Purple },
        future: { id: 'future', backgroundColor: DWB21_Blue },
        about99designs: { id: 'about-99designs', backgroundColor: DWB21_Green },
    };
    const sectionsData = Object.values(sections);
    const [navHeight, setNavHeight] = useState(80);

    const [introScrollToSection, setIntroScrollSection] = useState(
        sectionRefs.current[sections.diversity.id]
    );

    useEffect(() => {
        setIntroScrollSection(sectionRefs.current[sections.diversity.id]);
    }, [sections.diversity.id]);

    return (
        <>
            <Head currentDomain={site.getDomain()} />

            <Nav setNavHeight={setNavHeight} />

            <Desktop>
                <SideNavigation
                    sectionRefs={sectionRefs}
                    sectionsData={sectionsData}
                    navHeight={navHeight}
                />
            </Desktop>

            <div id={sections.intro.id} ref={(e) => (sectionRefs.current[sections.intro.id] = e)}>
                <Intro navHeight={navHeight} elementToScrollTo={introScrollToSection} />
            </div>
            <SectionWrapper
                id={sections.diversity.id}
                ref={(e) => (sectionRefs.current[sections.diversity.id] = e)}
                backgroundColor={sections.diversity.backgroundColor}
            >
                <Diversity />
            </SectionWrapper>
            <SectionWrapper
                id={sections.creativity.id}
                ref={(e) => (sectionRefs.current[sections.creativity.id] = e)}
                backgroundColor={sections.creativity.backgroundColor}
            >
                <Creativity />
            </SectionWrapper>
            <SectionWrapper
                id={sections.balance.id}
                ref={(e) => (sectionRefs.current[sections.balance.id] = e)}
                backgroundColor={sections.balance.backgroundColor}
            >
                <Balance />
            </SectionWrapper>
            <SectionWrapper
                id={sections.agencies.id}
                ref={(e) => (sectionRefs.current[sections.agencies.id] = e)}
                backgroundColor={sections.agencies.backgroundColor}
            >
                <Agencies />
            </SectionWrapper>
            <SectionWrapper
                id={sections.future.id}
                ref={(e) => (sectionRefs.current[sections.future.id] = e)}
                backgroundColor={sections.future.backgroundColor}
            >
                <Future />
            </SectionWrapper>
            <SectionWrapper
                id={sections.about99designs.id}
                ref={(e) => (sectionRefs.current[sections.about99designs.id] = e)}
                backgroundColor={sections.about99designs.backgroundColor}
            >
                <About99designs />
            </SectionWrapper>
            <div className="app max--widescreen transparent-site-header">
                <SiteFooterStyleOverride site={site}></SiteFooterStyleOverride>
            </div>
            <Box height={spaces['1.5bu']} background={colors.grey100} />
        </>
    );
}

const STATIC_REVALIDATION_IN_SECONDS = 60 * 5;

export async function getStaticProps(context: GetStaticProps) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { locale } = context;
    const host = getHostForLocale(locale);
    const siteConfig = await loadSiteConfig(host);
    const translations = await loadTranslations(locale.slice(0, 2));

    return {
        props: {
            locale,
            host,
            siteConfig: JSON.stringify(siteConfig),
            translations: JSON.stringify(translations),
            experiments: '',
        },
        revalidate: STATIC_REVALIDATION_IN_SECONDS,
    };
}
